
import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import config from '../config/config';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();
  const navigate = useNavigate();

  const API_URL = config.API_URL;

  useEffect(() => {
    // Check if user token exists in localStorage
    const token = localStorage.getItem('token');
    const role = localStorage.getItem('role');
    
    if (token) {
      try {
        // Verify token expiration
        const decodedToken = jwtDecode(token);
        const currentTime = Date.now() / 1000;
        
        if (decodedToken.exp < currentTime) {
          // Token expired
          logout();
        } else {
          // Set authorization header
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          
          // Get user profile based on role
          const fetchUser = async () => {
            try {
              let res;
              if (role === 'candidate') {
                // Fetch candidate profile
                res = await axios.get(`${API_URL}/exam-candidate/profile`);
                setUser({
                  id: res.data._id,
                  name: res.data.name,
                  email: res.data.email,
                  role: 'candidate',
                  companyId: res.data.companyId,
                  status: res.data.status,
                  examHistory: res.data.examHistory,
                  // Keep current exam data for backwards compatibility
                  examId: res.data.examId,
                  dateOfExam: res.data.dateOfExam,
                  totalTimeAllotted: res.data.totalTimeAllotted,
                });
              } else {
                // Fetch admin profile
                res = await axios.get(`${API_URL}/auth/profile`);
                setUser({
                  id: res.data._id,
                  name: res.data.name,
                  email: res.data.email,
                  role: res.data.role,
                });
              }
            } catch (error) {
              console.error('Error fetching user profile:', error);
              logout();
            } finally {
              setIsLoading(false);
            }
          };
          
          fetchUser();
        }
      } catch (error) {
        console.error('Invalid token:', error);
        logout();
        setIsLoading(false);
      }
    } else {
      setIsLoading(false);
    }
  }, [API_URL]);

  // Login admin
  const login = async (email, password) => {
  try {
    const res = await axios.post(`${API_URL}/auth/login`, { email, password });
    
    localStorage.setItem('token', res.data.token);
    localStorage.setItem('role', res.data.role);
    axios.defaults.headers.common['Authorization'] = `Bearer ${res.data.token}`;
    
    const userData = {
      _id: res.data._id,
      name: res.data.name,
      email: res.data.email,
      role: res.data.role,
      is_first_login: res.data.is_first_login,  // <-- IMPORTANT
    };
    setUser(userData);
    
    toast({
      title: "Login successful",
      description: `Welcome back, ${res.data.name}!`,
    });
    
    return userData;  // <-- RETURN the full object
  } catch (error) {
    toast({
      title: "Login failed",
      description: error.response?.data?.message || "Invalid credentials",
      variant: "destructive",
    });
    return null;
  }
};


  // Login candidate (request OTP)
  const candidateLogin = async (email) => {
    try {
      const res = await axios.post(`${API_URL}/auth/candidate/login`, { email });
      
      toast({
        title: "OTP Sent",
        description: "Please check your email for the OTP",
      });
      
      return true;
    } catch (error) {
      toast({
        title: "Failed to send OTP",
        description: error.response?.data?.message || "Please check your email address",
        variant: "destructive",
      });
      return false;
    }
  };

  // Verify OTP
  const verifyOtp = async (email, otp) => {
    try {
      const res = await axios.post(`${API_URL}/auth/candidate/verify`, { email, otp });
      
      // Save token to localStorage
      localStorage.setItem('token', res.data.token);
      localStorage.setItem('role', res.data.role);
      
      // Set authorization header
      axios.defaults.headers.common['Authorization'] = `Bearer ${res.data.token}`;
      
      // Set user with complete candidate data
      setUser({
        id: res.data._id,
        name: res.data.name,
        email: res.data.email,
        role: res.data.role,
        companyId: res.data.companyId,
        status: res.data.status,
        examHistory: res.data.examHistory,
        // Keep current exam data for backwards compatibility
        examId: res.data.examId,
        dateOfExam: res.data.dateOfExam,
        totalTimeAllotted: res.data.totalTimeAllotted,
      });
      
      toast({
        title: "Login successful",
        description: `Welcome, ${res.data.name}!`,
      });
      
      return true;
    } catch (error) {
      toast({
        title: "Verification failed",
        description: error.response?.data?.message || "Invalid OTP",
        variant: "destructive",
      });
      return false;
    }
  };

  // Logout
  const logout = () => {
    // Remove token from localStorage
    localStorage.removeItem('token');
    localStorage.removeItem('role');
    
    // Remove authorization header
    delete axios.defaults.headers.common['Authorization'];
    
    // Clear user state
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, isLoading, login, candidateLogin, verifyOtp, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);